import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { BelenderWidgetResponse } from 'src/app/models/BelenderWidgetResponse';
import { CancelRequest } from 'src/app/models/CancelRequest';
import { CancelResponse } from 'src/app/models/CancelResponse';
import { FeasibilityData } from 'src/app/models/FeasibilityData';
import { LabeledValue } from 'src/app/models/LabeledValue';
import { ODDetailResponse } from 'src/app/models/OpenData.ODDetailResponse';
import { PersonQueryStatusResponse } from 'src/app/models/PersonQueryStatusResponse';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';
import { StandardRequestDetailDocumentsService } from './standard-request-detail-documents/standard-request-detail-documents.service';

@Injectable({
    providedIn: 'root'
})
export class StandardRequestDetailService {

    isLoading = false;
    request: ODDetailResponse;
    queryId: string;
    lastQueryHour: Date;
    isHistorySidebarVisible = false;
    isCalculateViabilitySidebarVisible = false;
    feasibilityData: FeasibilityData = {
        isCreditOperationMade: false,
        isCreditGranted: false,
        creditAmount: undefined
    };
    feasibilityEnrichmentData: Record<string, any> | undefined = undefined;

    constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly toastService: ToastService,
        private readonly standardRequestDetailDocumentsService: StandardRequestDetailDocumentsService,
        private readonly utilsService: UtilsService
    ) { }

    getBelenderWidget(param1: string, param2: string, param3: string): Promise<BelenderWidgetResponse> {
        return this.apiService.get(`requests/belender-widget?param1=${param1}&param2=${param2}&param3=${param3}`);
    }

    getRequestFullPersonName(): string {
        if (this.request != null) {
            return this.request.personQuery?.firstName + ' ' +
				this.request.personQuery?.firstSurname + ' ' +
				(this.request.personQuery.secondSurname ? this.request.personQuery.secondSurname : '');
        }
        return null;
    }

    async saveDocument(url: string, fileName: string): Promise<void> {
        const file = await this.apiService.postBlob(`files/amazon-file`, { url: url, fileName: fileName });
        saveAs(file, fileName);
    }

    saveJSON(json: object, fileName: string): void {
        // Convert the object to a JSON string
        const jsonString = JSON.stringify(json);
        // Create a Blob from the JSON string
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, fileName);
    }

    async getRequestFromUrl(withoutGuard = false): Promise<void> {
        this.queryId = this.validateURLAndGetRequestId(this.router.url);
        if (this.queryId) {
            await this.refreshRequest(withoutGuard);
        } else {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.NO_QUERY_ID', true);
        };
    }

    async refreshRequest(withoutGuard: boolean): Promise<void> {
        try {
            this.isLoading = true;
            this.standardRequestDetailDocumentsService.clearAccordion();

            await this.setRequest(withoutGuard);
            const personType : string = this.request.personQuery.personType;

            if(personType === "01"){
                await Promise.all([
                    this.setEnrichmentData(),
                    this.setConsentUrl()
                ]);    
            }else{
                await Promise.all([
                    this.setCotizacionesData(),
                    this.setLaboralData(),
                    this.setDelphiScoreData(),
                    this.setEnrichmentData(),
                    this.setConsentUrl()
                ]);
            }

            this.standardRequestDetailDocumentsService.setDocumentNames(this.request);
            this.standardRequestDetailDocumentsService.expandAll();
        } catch {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.ERROR_NOT_FOUND', true);
        } finally {
            this.isLoading = false;
        }
    }

    private async setConsentUrl(): Promise<void> {
        if (!this.request.personQuery.consentUrlBoxed) {
            const req = { queryId: this.queryId };
            const res = await this.apiService.post<PersonQueryStatusResponse>('standard/person-query-status', req);
            this.request.personQuery.consentUrl = res.consentUrl;
            this.request.personQuery.consentUrlBoxed = res.consentUrlBoxed ? res.consentUrlBoxed : this.utilsService.convertConsentUrlToConsentUrlBoxed(res.consentUrl);
        }
    }

    cancelRequest(cancelRequest: CancelRequest): Promise<CancelResponse> {
        return this.apiService.post<CancelResponse>('standard/delete-request', cancelRequest);
    }

    private validateURLAndGetRequestId(url: string): string | null {
        const regex = /request-detail\/([0-9]{14}-[A-Z0-9]{18})/;
        const matches = url.match(regex);
        if (matches && matches[1]) {
            return matches[1];
        } else {
            return null;
        }
    }

    private async setRequest(withoutGuard: boolean) {
        if (withoutGuard) {
            this.request = await this.apiService.get<ODDetailResponse>(`standard/request-detail?queryId=${this.queryId}`);
        }

        if (this.request.data['Score']) {
            this.request.documents["Score"] = "";
        }

        if (!environment.production && !this.request.data['Calculos']) {
            this.request.data['Calculos'] = {};
        }

        await Promise.all([
            this.recalculateODIncomeTemporal(),
            this.recalculateBillingTemporal()
        ]);

        this.lastQueryHour = new Date();
    }

    private async recalculateODIncomeTemporal(): Promise<void> {
        if (this.request.data['Calculos']) {
            const odIncome = await this.apiService.post<number>('standard/od-income', this.request);
            this.request.data['Calculos']['OD_Ingresos'] = odIncome;
        }
    }

    private async recalculateBillingTemporal(): Promise<void> {
        if (this.request.data['Calculos']) {
            const billing = await this.apiService.post<number>('standard/billing', this.request);
            this.request.data['Calculos']['Facturacion'] = billing;
        }
    }

    private async setLaboralData(): Promise<void> {
        const laboralData = await this.apiService.post<LabeledValue[]>('standard/data/laboral', this.request);
        if(laboralData.length > 0) {
            this.standardRequestDetailDocumentsService.addDataToAccordion('REQUEST_DETAIL.LABORAL.RESUME', laboralData);
        }
    }

    private async setCotizacionesData(): Promise<void> {
        const cotizacionData = await this.apiService.post<LabeledValue[]>('standard/data/cotizaciones', this.request);
        if(cotizacionData.length > 0) {
            this.standardRequestDetailDocumentsService.addDataToAccordion('REQUEST_DETAIL.COTIZACIONES.RESUME', cotizacionData);
        }
    }

    private async setDelphiScoreData(): Promise<void> {
        const scoreDelphiData = await this.apiService.post<LabeledValue[]>('standard/data/delphi-score', this.request);
        if(scoreDelphiData.length > 0) {
            this.standardRequestDetailDocumentsService.addDataToAccordion('REQUEST_DETAIL.SCORE.RESUME', scoreDelphiData);
        }
    }

    private async setEnrichmentData(): Promise<void> {
        const enrichmentData = await this.apiService.post<Record<string, LabeledValue[]>>('standard/data/enrichment', this.request);

        for (const key in enrichmentData) {
            const items = enrichmentData[key];
            this.standardRequestDetailDocumentsService.addDataToAccordion(key, items);
        }
    }

    resetFeasibilityData(): void {
        this.feasibilityEnrichmentData = undefined;
        this.feasibilityData = {
            isCreditOperationMade: false,
            isCreditGranted: false,
            creditAmount: undefined
        };
    }

    showDocumentDetails(name: string): boolean {
        return !(name === 'Score' && this.request.data['Score'] === null);
    }
}
